import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useTranslation } from '../hooks/useTranslation';
import {
  Shield,
  Info,
  CloudUpload,
  Psychology,
  Share,
  Security,
  Language,
  Assignment,
  SupervisedUserCircle,
  Cookie,
  ChildCare,
  Fingerprint,
  HealthAndSafety,
  Update,
  Email,
  Gavel,
  CheckCircle,
  ExpandMore,
  ExpandLess,
  Warning,
  Lock,
  Groups,
  Storage
} from '../mui-icons';
import './PrivacyPolicyPage.css';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [expandedSection, setExpandedSection] = useState(null);
  const [activeSection, setActiveSection] = useState('infoCollected');
  const sectionRefs = useRef({});

  // 16 seções da Privacy Policy
  const sections = [
    { id: 'infoCollected', icon: <Info />, color: '#2196F3' },
    { id: 'howCollect', icon: <CloudUpload />, color: '#9C27B0' },
    { id: 'purpose', icon: <Psychology />, color: '#4CAF50' },
    { id: 'sharing', icon: <Share />, color: '#FF9800' },
    { id: 'storage', icon: <Storage />, color: '#00BCD4' },
    { id: 'international', icon: <Language />, color: '#3F51B5' },
    { id: 'rights', icon: <Assignment />, color: '#E91E63' },
    { id: 'dpo', icon: <SupervisedUserCircle />, color: '#607D8B' },
    { id: 'cookies', icon: <Cookie />, color: '#FF5722' },
    { id: 'minors', icon: <ChildCare />, color: '#009688' },
    { id: 'biometry', icon: <Fingerprint />, color: '#795548' },
    { id: 'healthAI', icon: <HealthAndSafety />, color: '#F44336' },
    { id: 'changes', icon: <Update />, color: '#673AB7' },
    { id: 'contact', icon: <Email />, color: '#00BCD4' },
    { id: 'legislation', icon: <Gavel />, color: '#4CAF50' },
    { id: 'consent', icon: <CheckCircle />, color: '#2196F3' }
  ];

  // Scroll to section and expand
  const scrollToSection = (sectionId) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setExpandedSection(sectionId);
      setActiveSection(sectionId);
    }
  };

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  // Intersection Observer para destacar seção ativa no menu
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -80% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.dataset.section);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Renderizar seções conforme conteúdo
  const renderInfoCollectedSection = () => {
    const data = t('privacyPolicy.sections.infoCollected');
    return (
      <div className="section-content">
        <div className="subsection-block">
          <h3 className="subsection-title">{data.personalData.title}</h3>
          <ul className="privacy-list">
            {data.personalData.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="subsection-block">
          <h3 className="subsection-title">{data.healthData.title}</h3>
          <p className="subsection-description">{data.healthData.description}</p>
          <ul className="privacy-list">
            {data.healthData.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="subsection-block">
          <h3 className="subsection-title">{data.usageData.title}</h3>
          <ul className="privacy-list">
            {data.usageData.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="subsection-block">
          <h3 className="subsection-title">{data.technicalData.title}</h3>
          <ul className="privacy-list">
            {data.technicalData.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderHowCollectSection = () => {
    const data = t('privacyPolicy.sections.howCollect');
    return (
      <div className="section-content">
        <p className="section-intro">{data.description}</p>
        <ul className="privacy-list">
          {data.methods.map((method, index) => (
            <li key={index}>{method}</li>
          ))}
        </ul>
      </div>
    );
  };

  const renderPurposeSection = () => {
    const data = t('privacyPolicy.sections.purpose');
    return (
      <div className="section-content">
        {data.categories.map((category, index) => (
          <div key={index} className="subsection-block">
            <h3 className="subsection-title">{category.title}</h3>
            <ul className="privacy-list">
              {category.items.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  const renderSharingSection = () => {
    const data = t('privacyPolicy.sections.sharing');
    return (
      <div className="section-content">
        <div className="subsection-block">
          <h3 className="subsection-title">{data.partners.title}</h3>
          {data.partners.list.map((partner, index) => (
            <div key={index} className="partner-card">
              <h4 className="partner-name">{partner.name}</h4>
              <div className="partner-details">
                <p><strong>{data.partners.labels.purpose}:</strong> {partner.purpose}</p>
                <p><strong>{data.partners.labels.dataShared}:</strong> {partner.dataShared}</p>
                <p><strong>{data.partners.labels.location}:</strong> {partner.location}</p>
                {partner.policy && (
                  <p><strong>{data.partners.labels.policy}:</strong> <a href={partner.policy} target="_blank" rel="noopener noreferrer">{partner.policy}</a></p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="warning-box">
          <Warning sx={{ fontSize: 32, color: '#4CAF50' }} />
          <div>
            <h4 className="warning-title">{data.noSelling.title}</h4>
            <p className="warning-text">{data.noSelling.description}</p>
          </div>
        </div>

        <div className="subsection-block">
          <h3 className="subsection-title">{data.aggregated.title}</h3>
          <p className="subsection-description">{data.aggregated.description}</p>
          <ul className="privacy-list green-checks">
            {data.aggregated.guarantees.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
          <div className="examples-box">
            <div className="example-good">
              <CheckCircle sx={{ fontSize: 20, marginRight: '8px', color: '#4CAF50' }} />
              <div>
                <strong>{data.aggregated.goodExample.label}:</strong>
                <p>{data.aggregated.goodExample.text}</p>
              </div>
            </div>
            <div className="example-bad">
              <Warning sx={{ fontSize: 20, marginRight: '8px', color: '#F44336' }} />
              <div>
                <strong>{data.aggregated.badExample.label}:</strong>
                <p className="strikethrough">{data.aggregated.badExample.text}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="subsection-block">
          <h3 className="subsection-title">{data.legalBasis.title}</h3>
          <ul className="privacy-list">
            {data.legalBasis.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderStorageSection = () => {
    const data = t('privacyPolicy.sections.storage');
    return (
      <div className="section-content">
        <div className="subsection-block">
          <h3 className="subsection-title">{data.security.title}</h3>
          <ul className="privacy-list">
            {data.security.measures.map((measure, index) => (
              <li key={index}>{measure}</li>
            ))}
          </ul>
        </div>

        <div className="subsection-block">
          <h3 className="subsection-title">{data.retention.title}</h3>
          <div className="retention-table">
            {data.retention.periods.map((period, index) => (
              <div key={index} className="retention-item">
                <span className="retention-type">{period.type}</span>
                <span className="retention-duration">{period.duration}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="subsection-block">
          <h3 className="subsection-title">{data.location.title}</h3>
          <p className="subsection-description">{data.location.description}</p>
        </div>
      </div>
    );
  };

  const renderInternationalSection = () => {
    const data = t('privacyPolicy.sections.international');
    return (
      <div className="section-content">
        <p className="section-intro">{data.intro}</p>
        <div className="subsection-block">
          <h3 className="subsection-title">{data.destinations.title}</h3>
          <ul className="privacy-list">
            {data.destinations.list.map((dest, index) => (
              <li key={index}>{dest}</li>
            ))}
          </ul>
        </div>
        <div className="subsection-block">
          <h3 className="subsection-title">{data.guarantees.title}</h3>
          <ul className="privacy-list green-checks">
            {data.guarantees.list.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderRightsSection = () => {
    const data = t('privacyPolicy.sections.rights');
    return (
      <div className="section-content">
        <p className="section-intro">{data.intro}</p>
        <div className="rights-grid">
          {data.list.map((right, index) => (
            <div key={index} className="right-card">
              <CheckCircle sx={{ fontSize: 24, color: '#4CAF50', marginBottom: '8px' }} />
              <h4 className="right-title">{right.title}</h4>
              <p className="right-description">{right.description}</p>
            </div>
          ))}
        </div>
        <div className="subsection-block">
          <h3 className="subsection-title">{data.howToExercise.title}</h3>
          <div className="contact-methods">
            <p><strong>{data.howToExercise.email.label}:</strong></p>
            <ul className="privacy-list">
              {data.howToExercise.email.addresses.map((email, index) => (
                <li key={index}><a href={`mailto:${email}`}>{email}</a></li>
              ))}
            </ul>
            <p><strong>{data.howToExercise.app.label}:</strong> {data.howToExercise.app.path}</p>
            <p><strong>{data.howToExercise.deadline.label}:</strong> {data.howToExercise.deadline.time}</p>
            <p><strong>{data.howToExercise.form.label}:</strong> <a href={data.howToExercise.form.url} target="_blank" rel="noopener noreferrer">{data.howToExercise.form.url}</a></p>
          </div>
        </div>
      </div>
    );
  };

  const renderDPOSection = () => {
    const data = t('privacyPolicy.sections.dpo');
    return (
      <div className="section-content">
        <p className="section-intro">{data.intro}</p>
        <div className="dpo-card">
          <SupervisedUserCircle sx={{ fontSize: 48, color: '#607D8B', marginBottom: '16px' }} />
          <div className="dpo-details">
            <p><strong>{data.name.label}:</strong> {data.name.value}</p>
            <p><strong>{data.email.label}:</strong> <a href={`mailto:${data.email.value}`}>{data.email.value}</a></p>
            <p><strong>{data.address.label}:</strong> {data.address.value}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderCookiesSection = () => {
    const data = t('privacyPolicy.sections.cookies');
    return (
      <div className="section-content">
        <div className="subsection-block">
          <h3 className="subsection-title">{data.types.title}</h3>
          {data.types.list.map((cookieType, index) => (
            <div key={index} className="cookie-type-card">
              <h4 className="cookie-type-title">{cookieType.name}</h4>
              <p><strong>{data.types.labels.purpose}:</strong> {cookieType.purpose}</p>
              <p><strong>{data.types.labels.data}:</strong> {cookieType.data}</p>
              <p><strong>{data.types.labels.disable}:</strong> {cookieType.disable}</p>
            </div>
          ))}
        </div>
        <div className="subsection-block">
          <h3 className="subsection-title">{data.manage.title}</h3>
          <ul className="privacy-list">
            {data.manage.options.map((option, index) => (
              <li key={index}>{option}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderMinorsSection = () => {
    const data = t('privacyPolicy.sections.minors');
    return (
      <div className="section-content">
        <p className="section-intro">{data.ageLimit}</p>
        <p className="subsection-description">{data.consent}</p>
        <p className="subsection-description">{data.removal}</p>
      </div>
    );
  };

  const renderBiometrySection = () => {
    const data = t('privacyPolicy.sections.biometry');
    return (
      <div className="section-content">
        <div className="subsection-block">
          <h3 className="subsection-title">{data.howWorks.title}</h3>
          <ul className="privacy-list">
            {data.howWorks.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
        <div className="subsection-block">
          <h3 className="subsection-title">{data.control.title}</h3>
          <ul className="privacy-list">
            {data.control.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderHealthAISection = () => {
    const data = t('privacyPolicy.sections.healthAI');
    return (
      <div className="section-content">
        <div className="subsection-block">
          <h3 className="subsection-title">{data.aiUsage.title}</h3>
          <p className="subsection-description">{data.aiUsage.description}</p>
          <ul className="privacy-list">
            {data.aiUsage.features.map((feature, index) => (
              <li key={index}>{feature}</li>
            ))}
          </ul>
          <p className="subsection-description">{data.aiUsage.proprietary}</p>
        </div>

        <div className="warning-box important">
          <Warning sx={{ fontSize: 32, color: '#FF9800' }} />
          <div>
            <h4 className="warning-title">{data.disclaimer.title}</h4>
            <p className="warning-text">{data.disclaimer.text}</p>
            <p className="warning-subtitle">{data.disclaimer.consult}</p>
            <ul className="privacy-list">
              {data.disclaimer.cases.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="subsection-block">
          <h3 className="subsection-title">{data.limitations.title}</h3>
          <ul className="privacy-list">
            {data.limitations.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderChangesSection = () => {
    const data = t('privacyPolicy.sections.changes');
    return (
      <div className="section-content">
        <p className="section-intro">{data.intro}</p>
        <div className="subsection-block">
          <h3 className="subsection-title">{data.notification.title}</h3>
          <ul className="privacy-list">
            {data.notification.methods.map((method, index) => (
              <li key={index}>{method}</li>
            ))}
          </ul>
        </div>
        <div className="subsection-block">
          <h3 className="subsection-title">{data.effective.title}</h3>
          <p className="subsection-description">{data.effective.description}</p>
        </div>
      </div>
    );
  };

  const renderContactSection = () => {
    const data = t('privacyPolicy.sections.contact');
    return (
      <div className="section-content">
        <p className="section-intro">{data.intro}</p>
        <div className="company-card">
          <Shield sx={{ fontSize: 48, color: '#4CAF50', marginBottom: '16px' }} />
          <div className="company-details">
            <p className="company-name">{data.company.name}</p>
            <p><strong>{data.company.cnpj.label}:</strong> {data.company.cnpj.value}</p>
            <p><strong>{data.company.email.label}:</strong> <a href={`mailto:${data.company.email.value}`}>{data.company.email.value}</a></p>
            <p><strong>{data.company.address.label}:</strong> {data.company.address.value}</p>
            <p><strong>{data.company.website.label}:</strong> <a href={data.company.website.value} target="_blank" rel="noopener noreferrer">{data.company.website.value}</a></p>
            <p><strong>{data.company.support.label}:</strong> {data.company.support.value}</p>
            <p><strong>{data.company.hours.label}:</strong> {data.company.hours.value}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderLegislationSection = () => {
    const data = t('privacyPolicy.sections.legislation');
    return (
      <div className="section-content">
        <p className="section-intro">{data.intro}</p>
        <ul className="privacy-list">
          {data.laws.map((law, index) => (
            <li key={index}>{law}</li>
          ))}
        </ul>
        <p className="subsection-description"><strong>{data.forum.label}:</strong> {data.forum.value}</p>
      </div>
    );
  };

  const renderConsentSection = () => {
    const data = t('privacyPolicy.sections.consent');
    return (
      <div className="section-content">
        <p className="section-intro">{data.agreement}</p>
        <p className="subsection-description">{data.disagree}</p>
      </div>
    );
  };

  // Mapeamento das funções de renderização
  const sectionRenderers = {
    infoCollected: renderInfoCollectedSection,
    howCollect: renderHowCollectSection,
    purpose: renderPurposeSection,
    sharing: renderSharingSection,
    storage: renderStorageSection,
    international: renderInternationalSection,
    rights: renderRightsSection,
    dpo: renderDPOSection,
    cookies: renderCookiesSection,
    minors: renderMinorsSection,
    biometry: renderBiometrySection,
    healthAI: renderHealthAISection,
    changes: renderChangesSection,
    contact: renderContactSection,
    legislation: renderLegislationSection,
    consent: renderConsentSection
  };

  return (
    <div className="privacy-policy-page surface-marketing">
      <Header />

      {/* Hero Section */}
      <section className="privacy-hero">
        <div className="container">
          <div className="privacy-hero-content">
            <Shield sx={{ fontSize: 80, marginBottom: '16px' }} />
            <h1 className="privacy-hero-title">{t('privacyPolicy.hero.title')}</h1>
            <p className="privacy-hero-subtitle">{t('privacyPolicy.hero.subtitle')}</p>
            <p className="privacy-hero-date">{t('privacyPolicy.hero.lastUpdate')}</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="privacy-layout">
        <div className="container privacy-container">
          {/* Sidebar Navigation */}
          <aside className="privacy-sidebar">
            <nav className="sidebar-nav">
              {sections.map((section) => (
                <button
                  key={section.id}
                  className={`sidebar-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(section.id)}
                  style={{ '--section-color': section.color }}
                >
                  <div className="sidebar-icon" style={{ color: section.color }}>
                    {section.icon}
                  </div>
                  <span className="sidebar-label">{t(`privacyPolicy.menu.${section.id}`)}</span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Content Area */}
          <main className="privacy-content">
            {sections.map((section) => (
              <div
                key={section.id}
                ref={(el) => (sectionRefs.current[section.id] = el)}
                data-section={section.id}
                className="section-wrapper"
              >
                <div
                  className={`section-card ${expandedSection === section.id ? 'expanded' : ''}`}
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="section-header">
                    <div className="section-header-left">
                      <div className="section-icon" style={{ backgroundColor: section.color }}>
                        {section.icon}
                      </div>
                      <h2 className="section-title">
                        {t(`privacyPolicy.sections.${section.id}.title`)}
                      </h2>
                    </div>
                    <div className="section-toggle">
                      {expandedSection === section.id ? (
                        <ExpandLess sx={{ fontSize: 32 }} />
                      ) : (
                        <ExpandMore sx={{ fontSize: 32 }} />
                      )}
                    </div>
                  </div>

                  {expandedSection === section.id && (
                    <div className="section-body" onClick={(e) => e.stopPropagation()}>
                      {sectionRenderers[section.id]()}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;

